/* ══════════════════════════════════════════════════════════════════
   Eine Zahl, die jemand abliest und eintippt — Zählerstand, Messwert.

   Geprüft am 17.09.2026 auf 390 px: Der Vorschlag im Zählerfeld stand als
   `1284.5` da, mit Punkt, und das Feld las nur ein einzelnes Komma — ein
   `1.284,5`, so wie die Anwendung Zahlen überall sonst schreibt, wurde zu
   „nicht abgelesen". Die Tastatur des Telefons liefert je nach Sprache
   Komma oder Punkt; beides muss gehen.

   **Seit dem 03.10.2026 die eine Lesung für jedes Zahlenfeld** — Beträge,
   Sätze, Zähler, Liter, Kilometer. Bis dahin hatte jede Maske ihre eigene
   (`replace(",", ".")` an siebzehn Stellen), und jede las etwas anderes:
   Das Kreditlimit `1.000,50` wurde zu 1 € (Playbook K-12), die Korrektur
   der Betriebsstunden schickte `1.234,5` als leer und löschte den Zähler
   (PR-03), und `alsZahl` in `bausteine/index.tsx` machte aus `48.50`
   4 850. Wer eine Zahl aus einem Feld liest, liest sie hier.

   **Unlesbar ist nicht leer** (`zahlUnlesbar`): Ein Feld mit `1,2,3` darin
   wird rot und schickt nichts — es schickt nie `null`, denn `null` heißt
   „gelöscht".

   ── Wie gelesen wird ─────────────────────────────────────────────

   | getippt     | gelesen  | warum |
   |-------------|----------|-------|
   | `1284,5`    | 1284,5   | das deutsche Komma |
   | `1.284,5`   | 1284,5   | mit Komma ist jeder Punkt ein Tausenderpunkt |
   | `1284.5`    | 1284,5   | ohne Komma ist ein Punkt das Komma der Tastatur |
   | `1.284`     | 1284     | Punkt und genau drei Ziffern: so schreibt die Anwendung selbst |
   | leer        | nichts   | nicht abgelesen — nicht null |

   | `0.125`     | 0,125    | eine führende Null ist keine Tausendergruppe |

   Die vierte Zeile ist eine Entscheidung: Ein Zähler mit drei Stellen hinter
   dem Komma kommt nicht vor, ein abgeschriebenes `1.284` sehr wohl.

   **Ins Feld geschrieben wird ohne Tausenderpunkt** (`zahlFeldtext`): Ein
   Feld mit `1.284,5` darin gibt beim nächsten Tastendruck eine andere Zahl
   her, als daneben steht. Zum Lesen mit (`zahlAnzeige`).

   `de-DE` ist hier kein sichtbarer Text, sondern eine Zahlenordnung —
   dieselbe Überlegung wie bei `euro` in `bausteine/index.tsx`.
   ══════════════════════════════════════════════════════════════════ */

/** Was im Feld steht, als Zahl — `null` heißt leer oder nicht lesbar. */
export function zahlLesen(text: string): number | null {
  let roh = text.trim().replace(/\s/g, "");
  if (roh === "") return null;
  if (roh.includes(",")) {
    if (roh.indexOf(",") !== roh.lastIndexOf(",")) return null;
    roh = roh.replace(/\./g, "").replace(",", ".");
  } else if (/^-?[1-9]\d{0,2}(\.\d{3})+$/.test(roh)) {
    /* Eine führende Null ist keine Tausendergruppe: `0.125` ist ein Satz
       (seit 0214 trägt der Einzelpreis vier Stellen), nicht 125. */
    roh = roh.replace(/\./g, "");
  }
  const n = Number(roh);
  return Number.isFinite(n) ? n : null;
}

/** Warum ein Betragsfeld nichts schickt: keine Zahl, oder mehr als zwei Stellen. */
export type Betragsfehler = "unlesbar" | "drei_stellen";

/** Was in einem Betragsfeld steht — der Text, wie getippt, und was er bedeutet. */
export type Betragseingabe = {
  /** Genau das, was getippt wurde. Ein Betragsfeld schreibt nie während des Tippens um. */
  text: string;
  /** Die Zahl — `null`, wenn das Feld leer ist oder einen Fehler hat. */
  zahl: number | null;
  fehler: Betragsfehler | null;
};

/**
 * **Ein Betrag hat zwei Stellen — und eine dritte wird gesagt, nicht
 * verschluckt** (E11, Luca, 04.10.2026: *„wir wollen überall 2 Stellen"*;
 * Nachprüfung Runde 4, N4-01 und N4-06).
 *
 * Bis dahin schnitt `zweiStellen` jede dritte Ziffer schon beim Tippen ab,
 * und das Satzfeld der Anlieferung schrieb nach jedem Tastendruck den Betrag
 * neu hinein: „6" wurde sofort „6,00", die „5" danach war die dritte Stelle
 * und fiel weg — aus getippten **65** wurden **6,00**, ohne Satz, und „Preis
 * fehlt" war weg. „12,345" im Einzel-Feld ging still als 12,34 hinaus.
 *
 * Jetzt bleibt der Text, wie er getippt ist, und gelesen wird er hier:
 *
 * | getippt     | Zahl     | Fehler |
 * |-------------|----------|--------|
 * | `65`        | 65       | — |
 * | `65,5`      | 65,5     | — |
 * | `1.234,56`  | 1234,56  | — |
 * | `12,345`    | —        | `drei_stellen` — der Satz sagt es, nichts geht hinaus |
 * | `1,2,3`     | —        | `unlesbar` |
 * | leer        | —        | — (leer heißt „wieder der gerechnete") |
 *
 * Ein Feld mit Fehler schickt nichts — es löscht auch nichts: Ein „Preis
 * fehlt" bleibt stehen, bis ein lesbarer Betrag dasteht. Die API weist eine
 * dritte Stelle ohnehin ab (`betrag.zwei_stellen`).
 *
 * Nur für Preise und Beträge. Mengen, Zähler und Prozentsätze dürfen mehr.
 */
export function betragEingabe(text: string): Betragseingabe {
  const zahl = zahlLesen(text);
  if (text.trim() === "") return { text, zahl: null, fehler: null };
  if (zahl === null) return { text, zahl: null, fehler: "unlesbar" };
  const cent = zahl * 100;
  if (Math.abs(Math.round(cent) - cent) > 1e-6) return { text, zahl: null, fehler: "drei_stellen" };
  return { text, zahl, fehler: null };
}

/**
 * **Was beim Verlassen eines Betragsfelds geschieht** — die eine Regel für
 * jedes Feld, das seinen Betrag selbst schreibt (N5-02, 04.10.2026).
 *
 * Geschrieben wird **beim Verlassen, nicht beim Tippen**. Das Satzfeld der
 * Anlieferung schrieb jeden lesbaren Zwischenstand: Auf dem Weg zu „12,345"
 * lag „12,34", der Posten rechnete schon damit, und beim Verlassen schrieb
 * das Feld den gespeicherten Satz zurück — „12,34", Satz weg, nichts rot.
 * Die Positionen im Angebot und das Posten-Blatt schrieben schon immer erst
 * beim Verlassen; jetzt alle.
 *
 * | Text       | danach im Feld | geschrieben |
 * |------------|----------------|-------------|
 * | `65,5`     | `65,50`        | 65,5 (wenn es nicht schon dasteht) |
 * | leer       | leer           | „wieder der gerechnete" (wenn etwas dastand) |
 * | `12,345`   | `12,345`, rot  | nichts — der letzte bestätigte Satz bleibt |
 * | `65,5,0`   | `65,5,0`, rot  | nichts |
 */
export function betragVerlassen(
  text: string,
  gespeichert: number | null | undefined,
): { text: string; schreiben: boolean; zahl: number | null } {
  const eingabe = betragEingabe(text);
  if (eingabe.fehler !== null) return { text, schreiben: false, zahl: null };
  if (eingabe.zahl === null) {
    return { text: "", schreiben: gespeichert !== null && gespeichert !== undefined, zahl: null };
  }
  return {
    text: betragFeldtext(eingabe.zahl),
    schreiben: eingabe.zahl !== gespeichert,
    zahl: eingabe.zahl,
  };
}

/**
 * **Ein gespeicherter Betrag, wie er in einem Betragsfeld steht** — der eine
 * Baustein dafür (N6-01, Nachprüfung Runde 6, 04.10.2026).
 *
 * Bis dahin schrieb jede Maske ihren eigenen: Das Posten-Blatt und das
 * Kreditlimit zeigten „65,00", die Geräteakte (Anschaffungswert,
 * Einkaufspreis) und die Positionen im Angebot (Einzel, Nachlass in Euro)
 * „65" bzw. „6" — derselbe gespeicherte Betrag, zweimal anders. Wer „65"
 * sieht, weiß nicht, ob das Feld ihn schon als Euro übernommen hat.
 *
 * | gespeichert | im Feld     | warum |
 * |-------------|-------------|-------|
 * | 65          | `65,00`     | zwei Stellen, Komma |
 * | 6           | `6,00`      | |
 * | 1250.5      | `1250,50`   | **ohne Tausenderpunkt** — wie jedes Feld (`zahlFeldtext`); gelesen wird `1.250,50` trotzdem |
 * | 0.125       | `0,125`     | ein Satz mit mehr Stellen (seit 0214 trägt der Einzelpreis vier) wird nie still gerundet — sonst schriebe das bloße Verlassen 0,13 zurück |
 * | leer/`null` | leer        | „nicht gesetzt" ist nicht 0,00 |
 *
 * Zum Lesen außerhalb eines Felds gilt weiter `euro` (mit Tausenderpunkt).
 */
export function betragFeldtext(n: number | null | undefined): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return "";
  const cent = n * 100;
  if (Math.abs(Math.round(cent) - cent) < 1e-6) return n.toFixed(2).replace(".", ",");
  return n.toFixed(4).replace(/0+$/, "").replace(".", ",");
}

/**
 * Dasselbe für ein `<input type="number">` (Preisstamm: Leistungen,
 * Zuschläge): Dort steht der Wert immer mit Punkt; der Browser zeigt ihn in
 * der Sprache der Oberfläche. Was nicht als Betrag lesbar ist, bleibt, wie
 * getippt — der Satz darunter sagt es (`betragFehlerNummernfeld`).
 */
export function betragNummernwert(text: string): string {
  if (text.trim() === "" || betragFehlerNummernfeld(text) !== null) return text;
  return betragFeldtext(Number(text.trim().replace(",", "."))).replace(",", ".");
}

/** Kurzform: der Fehler eines Betragsfelds, oder `null`. */
export function betragFehler(text: string): Betragsfehler | null {
  return betragEingabe(text).fehler;
}

/**
 * Dasselbe für ein `<input type="number">`: Dort liefert der Browser den Wert
 * immer mit Punkt und ohne Tausenderpunkt — „12.345" ist 12,345 und nicht
 * zwölftausend, darum gilt hier nicht die Lesung von `zahlLesen`.
 */
export function betragFehlerNummernfeld(text: string): Betragsfehler | null {
  if (text.trim() === "") return null;
  const zahl = Number(text.trim().replace(",", "."));
  if (!Number.isFinite(zahl)) return "unlesbar";
  const cent = zahl * 100;
  return Math.abs(Math.round(cent) - cent) > 1e-6 ? "drei_stellen" : null;
}

/** Etwas steht im Feld, aber es ist keine Zahl — rot machen, nichts schicken. */
export function zahlUnlesbar(text: string): boolean {
  return text.trim() !== "" && zahlLesen(text) === null;
}

/** Dieselbe Grenze wie die API (`PATCH/POST …/orte`, K-19). */
export const KM_HOECHSTENS = 2000;

/**
 * **Eine Entfernung, die keine ist** (K-19, 03.10.2026): „abc" wurde still
 * zu leer, −5 und 99.999 gingen durch. Leer bleibt erlaubt.
 */
export function kmFehler(text: string): "unlesbar" | "bereich" | null {
  if (text.trim() === "") return null;
  const zahl = zahlLesen(text);
  if (zahl === null) return "unlesbar";
  return zahl < 0 || zahl > KM_HOECHSTENS ? "bereich" : null;
}

/**
 * **Ein Zählerstand, den die API nicht annimmt** (D-10, 03.10.2026): keine
 * Zahl — oder eine negative. `-3` stand rot im Blatt, „Fertig" schloss es
 * trotzdem, und erst `POST /vorbereitungen` antwortete 422 mit einem Satz,
 * der auf ein rot umrandetes Feld zeigte, das zugeklappt war.
 */
export function zaehlerUngueltig(text: string): boolean {
  const n = zahlLesen(text);
  return zahlUnlesbar(text) || (n !== null && n < 0);
}

/**
 * `1284.5` → `1.284,5` — zum Lesen. Mit `fest` stehen immer `stellen`
 * Nachkommastellen da: ein Betrag `1.000,50`, nicht `1.000,5` (K-12).
 */
export function zahlAnzeige(n: number, stellen = 2, fest = false): string {
  return n.toLocaleString("de-DE", {
    maximumFractionDigits: stellen,
    ...(fest ? { minimumFractionDigits: stellen } : {}),
  });
}

/**
 * `1284.5` → `1284,5` — zum Hineinschreiben in ein Feld. Mit `stellen`
 * fest: `1000.5` → `1000,50`. **Ohne Tausenderpunkt**, damit das Feld beim
 * nächsten Tastendruck dieselbe Zahl hergibt, die danebensteht — und mit
 * Komma: Ein Feld mit `1000.5` darin las nach dem Löschen der letzten Ziffer
 * `1000.` (K-12, Nachprüfung 03.10.2026).
 */
export function zahlFeldtext(n: number, stellen?: number): string {
  return (stellen === undefined ? String(n) : n.toFixed(stellen)).replace(".", ",");
}

/**
 * **Ein Betrag in der Preisliste** — immer zwei Stellen, mit Tausenderpunkt:
 * `220` → „220,00", `10.5` → „10,50", `1200` → „1.200,00".
 *
 * Bis zum 04.10.2026 schrieb die Preisliste einen ganzen Betrag kurz („220")
 * — „so liest sie jeder im Betrieb". Luca im zweiten Interview (F37, Befund
 * N6-01): auch dort „220,00", wie überall sonst. Ein Satz mit mehr als zwei
 * Stellen gibt es seit 0226 nicht mehr; käme einer, stünde er ungerundet da.
 */
export function listenbetrag(n: number | null | undefined): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return "";
  const cent = n * 100;
  const stellen = Math.abs(Math.round(cent) - cent) < 1e-6 ? 2 : 4;
  return n.toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: stellen });
}

/**
 * **Eine Menge in der Preisliste** — Freistunden, Mindeststunden: Sie sind
 * keine Euro und bleiben eine Zahl („8", „2,5"), F37.
 */
export function listenzahl(n: number | null | undefined): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return "";
  return n.toLocaleString("de-DE", { maximumFractionDigits: 2 });
}

/** Die Grenze eines Satzes im Preisstamm — dieselbe wie `pflichtBetrag` in der API. */
export const SATZ_HOECHSTENS = 10_000_000;

/**
 * Ob ein getippter Satz in den Preisstamm passt: lesbar, nicht negativ,
 * höchstens 10 Mio. (S-21, 03.10.2026 — bis dahin ging 99.999.999 an den
 * Server und kam als leeres, nicht markiertes Feld zurück).
 */
export function satzImRahmen(n: number): boolean {
  return Number.isFinite(n) && n >= 0 && n <= SATZ_HOECHSTENS;
}
